import { useInput } from "@redotech/react-util/form";
import { t, useTranslation } from "@redotech/redo-i18n";
import HeartHandIcon from "@redotech/redo-icons/heart-hand_filled.svg";
import LiquidonateLogo from "@redotech/redo-icons/liquidonate.svg";
import MailIcon from "@redotech/redo-icons/mail-01_filled.svg";
import { toast } from "@redotech/redo-web/alert";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { groupInput, input } from "@redotech/ui/form";
import { emailValidator } from "@redotech/web-util/form";
import { memo, useEffect, useState } from "react";

export interface DonationProviderSetup {
  businessName: string;
  businessPhone: string;
  website: string;
  address: {
    street1: string;
    street2: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

function translateRequiredFieldLabel(label: string): string {
  switch (label) {
    case "Business name":
      return t("Business name");
    case "Business phone":
      return t("Business phone");
    case "Street address":
      return t("Street address");
    case "City":
      return t("City");
    case "State":
      return t("State");
    case "ZIP / postal code":
      return t("ZIP / postal code");
    case "Country":
      return t("Country");
    case "First name":
      return t("First name");
    case "Last name":
      return t("Last name");
    case "Phone number":
      return t("Phone number");
    default:
      return label;
  }
}

const required =
  (label: string) =>
  (value: string): string[] =>
    value.trim().length === 0
      ? [
          t("{{label}} is required", {
            label: translateRequiredFieldLabel(label),
          }),
        ]
      : [];

const setupForm = groupInput({
  businessName: input<string>({ validator: required("Business name") }),
  businessPhone: input<string>({ validator: required("Business phone") }),
  website: input<string>({}),
  address: groupInput({
    street1: input<string>({ validator: required("Street address") }),
    street2: input<string>({}),
    city: input<string>({ validator: required("City") }),
    state: input<string>({ validator: required("State") }),
    zip: input<string>({ validator: required("ZIP / postal code") }),
    country: input<string>({ validator: required("Country") }),
  }),
  firstName: input<string>({ validator: required("First name") }),
  lastName: input<string>({ validator: required("Last name") }),
  email: input<string>({ validator: emailValidator }),
  phone: input<string>({ validator: required("Phone number") }),
});

interface DonationProviderSetupModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  defaults: DonationProviderSetup;
  onConfirm: (setup: DonationProviderSetup) => Promise<void>;
}

export const DonationProviderSetupModal = memo(
  function DonationProviderSetupModal({
    open,
    setOpen,
    defaults,
    onConfirm,
  }: DonationProviderSetupModalProps) {
    const { t } = useTranslation();
    const [pending, setPending] = useState(false);
    const [successEmail, setSuccessEmail] = useState<string | null>(null);
    const form = useInput(setupForm, defaults);

    useEffect(() => {
      if (!open) {
        setSuccessEmail(null);
      }
    }, [open]);
    const { inputs } = form;
    const address = inputs.address.inputs;

    const handleConfirm = async () => {
      setPending(true);
      try {
        await onConfirm(form.value);
        setSuccessEmail(form.value.email);
      } catch {
        toast(t("Failed to set up donation provider. Please try again."), {
          variant: "error",
        });
      } finally {
        setPending(false);
      }
    };

    const stateOf = (errors: readonly string[]) =>
      errors.length > 0 ? "error" : "default";
    const hasErrors = form.allErrors.length > 0;

    return (
      <>
        <RedoModal
          isOpen={open && !successEmail}
          modalSize="sm"
          onModalCloseRequested={() => setOpen(false)}
          primaryButton={{
            text: t("Set up donation provider"),
            onClick: handleConfirm,
            disabled: hasErrors || pending,
            pending,
          }}
          secondaryButton={{
            text: t("Cancel"),
            onClick: () => setOpen(false),
            disabled: pending,
          }}
          title={t("Set up donation provider")}
          TitleIcon={HeartHandIcon}
        >
          <Flex dir="column" gap="3xl" w="full">
            <Flex dir="column" gap="lg">
              <Text fontSize="sm" textColor="secondary">
                {t(
                  "Donation routing sends eligible returns to a nearby nonprofit through LiquiDonate. You will receive an email to create your account once you submit your details.",
                )}
              </Text>
              <LiquidonateLogo />
            </Flex>

            <Flex dir="column" gap="lg" w="full">
              <Text fontSize="xs" fontWeight="semibold" textColor="primary">
                {t("Business details")}
              </Text>
              <Flex dir="column" gap="md" w="full">
                <RedoTextInput
                  label={t("Business name")}
                  setValue={inputs.businessName.setValue}
                  size="sm"
                  state={stateOf(inputs.businessName.errors)}
                  value={inputs.businessName.value}
                />
                <RedoTextInput
                  label={t("Business phone")}
                  setValue={inputs.businessPhone.setValue}
                  size="sm"
                  state={stateOf(inputs.businessPhone.errors)}
                  value={inputs.businessPhone.value}
                />
                <RedoTextInput
                  label={t("Website")}
                  setValue={inputs.website.setValue}
                  size="sm"
                  state={stateOf(inputs.website.errors)}
                  value={inputs.website.value}
                />
              </Flex>
            </Flex>

            <Flex dir="column" gap="lg" w="full">
              <Text fontSize="xs" fontWeight="semibold" textColor="primary">
                {t("Business address")}
              </Text>
              <Flex dir="column" gap="md" w="full">
                <RedoTextInput
                  label={t("Street address")}
                  setValue={address.street1.setValue}
                  size="sm"
                  state={stateOf(address.street1.errors)}
                  value={address.street1.value}
                />
                <RedoTextInput
                  label={t("Apt, suite, etc. (optional)")}
                  setValue={address.street2.setValue}
                  size="sm"
                  state={stateOf(address.street2.errors)}
                  value={address.street2.value}
                />
                <Flex gap="md" w="full">
                  <RedoTextInput
                    label={t("City")}
                    setValue={address.city.setValue}
                    size="sm"
                    state={stateOf(address.city.errors)}
                    value={address.city.value}
                  />
                  <RedoTextInput
                    label={t("State / province")}
                    setValue={address.state.setValue}
                    size="sm"
                    state={stateOf(address.state.errors)}
                    value={address.state.value}
                  />
                </Flex>
                <Flex gap="md" w="full">
                  <RedoTextInput
                    label={t("ZIP / postal code")}
                    setValue={address.zip.setValue}
                    size="sm"
                    state={stateOf(address.zip.errors)}
                    value={address.zip.value}
                  />
                  <RedoTextInput
                    label={t("Country")}
                    setValue={address.country.setValue}
                    size="sm"
                    state={stateOf(address.country.errors)}
                    value={address.country.value}
                  />
                </Flex>
              </Flex>
            </Flex>

            <Flex dir="column" gap="lg" w="full">
              <Text fontSize="xs" fontWeight="semibold" textColor="primary">
                {t("Account contact")}
              </Text>
              <Flex dir="column" gap="md" w="full">
                <Flex gap="md" w="full">
                  <RedoTextInput
                    label={t("First name")}
                    setValue={inputs.firstName.setValue}
                    size="sm"
                    state={stateOf(inputs.firstName.errors)}
                    value={inputs.firstName.value}
                  />
                  <RedoTextInput
                    label={t("Last name")}
                    setValue={inputs.lastName.setValue}
                    size="sm"
                    state={stateOf(inputs.lastName.errors)}
                    value={inputs.lastName.value}
                  />
                </Flex>
                <RedoTextInput
                  label={t("Email")}
                  placeholder={t("you@example.com")}
                  setValue={inputs.email.setValue}
                  size="sm"
                  state={stateOf(inputs.email.errors)}
                  value={inputs.email.value}
                />
                <RedoTextInput
                  label={t("Phone number")}
                  setValue={inputs.phone.setValue}
                  size="sm"
                  state={stateOf(inputs.phone.errors)}
                  value={inputs.phone.value}
                />
              </Flex>
            </Flex>

            {hasErrors && (
              <Text fontSize="xs" textColor="error">
                {form.allErrors[0]}
              </Text>
            )}
          </Flex>
        </RedoModal>
        <RedoModal
          headerIconAlignment="above"
          isOpen={!!successEmail}
          modalSize="sm"
          onModalCloseRequested={() => {
            setSuccessEmail(null);
            setOpen(false);
          }}
          primaryButton={{
            text: t("Done"),
            onClick: () => {
              setSuccessEmail(null);
              setOpen(false);
            },
          }}
          theme="success"
          title={t("Success!")}
          TitleIcon={MailIcon}
        >
          <Text fontSize="sm" textColor="secondary">
            {t(
              "Check your email ({{email}}) to finish setting up your LiquiDonate account.",
              { email: successEmail },
            )}
          </Text>
        </RedoModal>
      </>
    );
  },
);
